import { useState } from "react";
import SearchSelect from "../SearchSelect";
import Modal from "../ui/Modal";

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Handle both ISO strings and regular date strings
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    // Return in YYYY-MM-DD format
    return date.toISOString().split('T')[0];
  };

const InvoiceModal = ({ invoice, clients, settings, onSave, onClose, onSendEmail }) => {
  const [formData, setFormData] = useState({
    _id: invoice?._id || '',
    clientId: invoice?.clientId || '',
    date: formatDateForInput(invoice?.date) || new Date().toISOString().split('T')[0],
    dueDate: formatDateForInput(invoice?.dueDate) || '',
    status: invoice?.status || 'draft',
    items: invoice?.items || [{ itemName: '', description: '', quantity: 1, price: 0 }],
    notes: invoice?.notes || '',
    discountPercentage: invoice?.discountPercentage || 0,
    discountAmount: invoice?.discountAmount || 0,
    // Enhanced fields
    paymentTerms: invoice?.paymentTerms || '',
    poNumber: invoice?.poNumber || '',
    // Recurring fields
    isRecurring: invoice?.isRecurring || false,
    recurringFrequency: invoice?.recurringFrequency || 'monthly',
    recurringEndDate: formatDateForInput(invoice?.recurringEndDate) || '',
    recurringCount: invoice?.recurringCount || '',
    lastGenerated: invoice?.lastGenerated || null,
    nextDueDate: formatDateForInput(invoice?.nextDueDate) || null
  });

  const [showRecurringOptions, setShowRecurringOptions] = useState(formData.isRecurring);
  const [discountMode, setDiscountMode] = useState('percentage'); // 'percentage' or 'amount'

  // Payment terms options
  const paymentTermsOptions = [
    { value: '', label: 'Select payment terms', days: 0 },
    { value: 'Net 7', label: 'Net 7 days', days: 7 },
    { value: 'Net 15', label: 'Net 15 days', days: 15 },
    { value: 'Net 30', label: 'Net 30 days', days: 30 },
    { value: 'Net 45', label: 'Net 45 days', days: 45 },
    { value: 'Net 60', label: 'Net 60 days', days: 60 },
    { value: 'Net 90', label: 'Net 90 days', days: 90 }
  ];

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { itemName: '', description: '', quantity: 1, price: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // If setting up recurring invoice, calculate next due date
    if (formData.isRecurring && !formData.nextDueDate) {
      const nextDate = calculateNextDueDate(formData.date, formData.recurringFrequency);
      formData.nextDueDate = nextDate;
    }

    onSave(formData);
  };

  const calculateNextDueDate = (startDate, frequency) => {
    const date = new Date(startDate);
    switch (frequency) {
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'quarterly':
        date.setMonth(date.getMonth() + 3);
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + 1);
        break;
      case 'biennial':
        date.setFullYear(date.getFullYear() + 2);
        break;
      default:
        date.setMonth(date.getMonth() + 1);
        break;
    }
    return date.toISOString().split('T')[0];
  };

  // Calculate due date based on payment terms
  const calculateDueDateFromPaymentTerms = (invoiceDate, paymentTerms) => {
    const selectedTerm = paymentTermsOptions.find(term => term.value === paymentTerms);
    if (!selectedTerm || selectedTerm.days === 0) {
      return '';
    }

    const date = new Date(invoiceDate);
    date.setDate(date.getDate() + selectedTerm.days);
    return date.toISOString().split('T')[0];
  };

  // Handle payment terms change
  const handlePaymentTermsChange = (value) => {
    const newDueDate = calculateDueDateFromPaymentTerms(formData.date, value);
    setFormData({
      ...formData,
      paymentTerms: value,
      dueDate: newDueDate || formData.dueDate
    });
  };

  // Handle invoice date change - recalculate due date if payment terms are set
  const handleInvoiceDateChange = (value) => {
    const newDueDate = formData.paymentTerms ?
      calculateDueDateFromPaymentTerms(value, formData.paymentTerms) :
      formData.dueDate;

    setFormData({
      ...formData,
      date: value,
      dueDate: newDueDate
    });
  };

  const handleSendEmail = () => {
    console.log('Sending invoice email...', {
      invoiceId: invoice?._id,
      clientId: formData.clientId,
      total: total
    });

    if (onSendEmail) {
      onSendEmail(formData, 'invoice');
    }
  };

  const formatCurrency = (amount) => `${settings.currency}${parseFloat(amount || 0).toFixed(2)}`;

  // Get client details for default payment terms
  const selectedClient = clients.find(c => c._id === formData.clientId);

  // Auto-set payment terms and due date when client changes
  const handleClientChange = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    const newFormData = { ...formData, clientId };

    if (client) {
      // Set payment terms from client default
      if (client.paymentTerms && !formData.paymentTerms) {
        newFormData.paymentTerms = client.paymentTerms;
      }

      // Calculate due date based on payment terms
      if (client.paymentTerms && formData.date) {
        const invoiceDate = new Date(formData.date);
        let daysToAdd = 0;

        switch (client.paymentTerms) {
          case 'Due on Receipt': daysToAdd = 0; break;
          case 'Net 7': daysToAdd = 7; break;
          case 'Net 15': daysToAdd = 15; break;
          case 'Net 30': daysToAdd = 30; break;
          case 'Net 45': daysToAdd = 45; break;
          case 'Net 60': daysToAdd = 60; break;
          case 'Net 90': daysToAdd = 90; break;
          default: daysToAdd = 30; break;
        }

        if (daysToAdd > 0) {
          const dueDate = new Date(invoiceDate);
          dueDate.setDate(dueDate.getDate() + daysToAdd);
          newFormData.dueDate = dueDate.toISOString().split('T')[0];
        }
      }
    }

    setFormData(newFormData);
  };

  const clientOptions = clients.map(client => ({
    value: client._id,
    label: client.name + (client.contact_person ? ` - ${client.contact_person}` : '')
  }));

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);

  // Calculate discount based on mode
  let discount = 0;
  let calculatedPercentage = formData.discountPercentage;

  if (discountMode === 'amount') {
    discount = Math.min(formData.discountAmount || 0, subtotal); // Don't allow discount > subtotal
    calculatedPercentage = subtotal > 0 ? (discount / subtotal) * 100 : 0;
  } else {
    discount = subtotal * (formData.discountPercentage || 0) / 100;
  }

  const afterDiscount = subtotal - discount;
  const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
  const total = afterDiscount + tax;

  const recurringFrequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly (3 months)' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'biennial', label: 'Every Two Years' }
  ];

return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <>
          {invoice ? `Edit Invoice: ${invoice.number}` : 'Create Invoice'}
          {formData.isRecurring && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">Recurring</span>}
        </>
      }
      size="xlarge"
      footer={
        <div className="flex gap-3">
          <button 
            type="button" 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" 
            onClick={onClose}
          >
            Cancel
          </button>
          {invoice && formData.status !== 'draft' && (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSendEmail}
            >
              <i className="fas fa-envelope mr-2"></i>
              Send Email
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={handleSubmit}
          >
            {invoice ? 'Update Invoice' : 'Create Invoice'}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="p-0">
          {/* Top Row */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
              <SearchSelect
                data={clientOptions}
                selectedValue={formData.clientId}
                onChange={handleClientChange}
                placeholder="Select or search client..."
                name="clientId"
                className="mb-3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.paymentTerms || selectedClient?.paymentTerms || ''}
                onChange={(e) => handlePaymentTermsChange(e.target.value)}
              >
                {paymentTermsOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.poNumber}
                onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Total Display Row */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Total</label>
            <div className="px-3 py-2 text-2xl font-bold text-blue-600 bg-gray-50 rounded-md">
              {formatCurrency(total)}
            </div>
          </div>

          {/* Date Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.date}
                onChange={(e) => handleInvoiceDateChange(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
                {formData.paymentTerms && (
                  <span className="text-gray-500 text-xs ml-2">
                    (Auto-calculated from {formData.paymentTerms})
                  </span>
                )}
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <div className="flex items-center">
                <input
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  type="checkbox"
                  id="isRecurring"
                  checked={formData.isRecurring}
                  onChange={(e) => {
                    setFormData({ ...formData, isRecurring: e.target.checked });
                    setShowRecurringOptions(e.target.checked);
                  }}
                />
                <label className="ml-2 block text-sm font-medium text-gray-900" htmlFor="isRecurring">
                  Recurring Invoice
                </label>
              </div>
            </div>
          </div>

          {/* Recurring Options */}
          {showRecurringOptions && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                <h6 className="text-sm font-medium text-gray-900 mb-0">Recurring Settings</h6>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={formData.recurringFrequency}
                      onChange={(e) => setFormData({ ...formData, recurringFrequency: e.target.value })}
                    >
                      {recurringFrequencyOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={formData.recurringEndDate}
                      onChange={(e) => setFormData({ ...formData, recurringEndDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Count (Optional)</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={formData.recurringCount}
                      onChange={(e) => setFormData({ ...formData, recurringCount: e.target.value })}
                      placeholder="e.g., 12"
                      min="1"
                    />
                  </div>
                  <div>
                    {formData.nextDueDate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Next Invoice</label>
                        <div className="px-3 py-2 text-sm text-gray-900 bg-gray-50 rounded-md">
                          {new Date(formData.nextDueDate).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Items and Notes */}
            <div className="lg:col-span-2">
              {/* Line Items */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">Line Items</label>
                  <button 
                    type="button" 
                    onClick={addItem} 
                    className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <i className="fas fa-plus mr-1"></i> Add Item
                  </button>
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Qty</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formData.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded mb-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Item"
                              value={item.itemName}
                              onChange={(e) => updateItem(index, 'itemName', e.target.value)}
                              required
                            />
                            <textarea
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Description"
                              rows="2"
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Price"
                              value={item.price}
                              onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <span className="font-medium text-gray-900">
                              {formatCurrency(item.quantity * item.price)}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={formData.items.length === 1}
                            >
                              <i className="fas fa-trash text-sm"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes, terms, or payment instructions..."
                />
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                  <h6 className="text-sm font-medium text-gray-900 mb-0">Invoice Summary</h6>
                </div>
                <div className="p-4">
                  {/* Discount Section */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Discount</label>
                      <div className="inline-flex rounded-md shadow-sm" role="group">
                        <input
                          type="radio"
                          className="sr-only"
                          name="discountMode"
                          id="discountPercentage"
                          checked={discountMode === 'percentage'}
                          onChange={() => setDiscountMode('percentage')}
                        />
                        <label 
                          className={`px-3 py-1 text-xs font-medium border rounded-l-md cursor-pointer ${
                            discountMode === 'percentage' 
                              ? 'bg-blue-50 text-blue-700 border-blue-300' 
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`} 
                          htmlFor="discountPercentage"
                        >
                          %
                        </label>

                        <input
                          type="radio"
                          className="sr-only"
                          name="discountMode"
                          id="discountAmount"
                          checked={discountMode === 'amount'}
                          onChange={() => setDiscountMode('amount')}
                        />
                        <label 
                          className={`px-3 py-1 text-xs font-medium border-l-0 border rounded-r-md cursor-pointer ${
                            discountMode === 'amount' 
                              ? 'bg-blue-50 text-blue-700 border-blue-300' 
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`} 
                          htmlFor="discountAmount"
                        >
                          {settings.currency}
                        </label>
                      </div>
                    </div>

                    {discountMode === 'percentage' ? (
                      <div className="flex">
                        <input
                          type="number"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.discountPercentage}
                          onChange={(e) => setFormData({ ...formData, discountPercentage: parseFloat(e.target.value) || 0 })}
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="0"
                        />
                        <span className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 rounded-r-md">%</span>
                      </div>
                    ) : (
                      <div className="flex">
                        <span className="inline-flex items-center px-3 py-2 border border-gray-300 bg-gray-50 text-gray-500 rounded-l-md">{settings.currency}</span>
                        <input
                          type="number"
                          className="flex-1 px-3 py-2 border border-l-0 border-gray-300 rounded-r-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={formData.discountAmount}
                          onChange={(e) => {
                            const amount = parseFloat(e.target.value) || 0;
                            setFormData({ ...formData, discountAmount: amount });
                          }}
                          min="0"
                          max={subtotal}
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    )}

                    {discountMode === 'amount' && formData.discountAmount > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        Equivalent to {(parseFloat(calculatedPercentage) || 0).toFixed(2)}% discount
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    {/* Subtotal */}
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Subtotal:</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(subtotal)}</span>
                    </div>

                    {/* Discount */}
                    {discount > 0 && (
                      <>
                        <div className="flex justify-between items-center mb-2 text-red-600">
                          <span className="text-sm">Discount ({(parseFloat(calculatedPercentage) || 0).toFixed(2)}%):</span>
                          <span className="text-sm font-medium">-{formatCurrency(discount)}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">After Discount:</span>
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(afterDiscount)}</span>
                        </div>
                      </>
                    )}

                    {/* Tax */}
                    {!settings.taxExempt && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Tax ({settings.taxPercentage}%):</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(tax)}</span>
                      </div>
                    )}

                    <div className="border-t border-gray-200 pt-2 mt-2">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-base font-semibold text-gray-900">Total:</span>
                        <span className="text-xl font-bold text-blue-600">{formatCurrency(total)}</span>
                      </div>
                    </div>

                    {selectedClient && (
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>Client: {selectedClient.name}</div>
                        <div>Terms: {selectedClient.paymentTerms || 'Default'}</div>
                        <div>Currency: {selectedClient.currency || settings.currency}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default InvoiceModal;